// ─────────────────────────────────────────────────────────────────────────────
// Cockpit BM (bêta) — données de démo + helpers métier purs.
// Porté du prototype validé « Skilldeck nouveau Dashboard v2.html ».
// Persistance localStorage pour cette bêta ; le branchement Supabase est
// l'étape suivante (voir docs/ROADMAP_COPILOTE_BM.md, phase 1).
// Hiérarchie compte à 4 niveaux :
//   Compte (clients[]) › Entité (.clients[]) › Sous-entité (.entities[]) › Équipe (.subs[])
//   + domaines optionnels par équipe (.domains[]), contact optionnel par équipe.
// ─────────────────────────────────────────────────────────────────────────────

export const STAGES = [
  { id: 'identifie', label: 'Identifié', color: '#94929f', proba: 0.10 },
  { id: 'envoye', label: 'DC envoyé', color: '#60A5FA', proba: 0.30 },
  { id: 'entretien', label: 'Entretien', color: '#FACC15', proba: 0.60 },
  { id: 'nego', label: 'Négociation', color: '#A099E8', proba: 0.80 },
  { id: 'gagne', label: 'Gagné', color: '#4ADE80', proba: 1 },
  { id: 'perdu', label: 'Perdu', color: '#F87171', proba: 0 },
];
export const SOURCES = ['Mail', 'Free-Work', 'Malt', 'Portail client', 'Partenaire', 'LinkedIn', 'Autre'];
export const OWNERS = ['Tim', 'Romain'];
export const STATUTS = {
  Disponible: '#4ADE80',
  Intercontrat: '#F87171',
  'Bientôt dispo': '#FACC15',
  'En mission': '#9d9bc0',
};
export const SLSTATUS = { Brouillon: '#FACC15', 'Envoyée': '#60A5FA', 'Clôturée': '#4ADE80' };
export const CANDST = { proposé: '#9d9bc0', retenu: '#4ADE80', écarté: '#F87171' };
export const RELANCE_DAYS = 4;
export const INTERCO_DAYS = 30;

export const stageOf = (id) => STAGES.find((s) => s.id === id);
export const fmt = (n) => (n >= 1000 ? (n / 1000).toFixed(n % 1000 ? 1 : 0) + ' k€' : Math.round(n) + ' €');
export const daysLeft = (d) => (d ? Math.ceil((new Date(d) - new Date()) / 86400000) : null);
export const initials = (s) => (s || '?').split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase();

export const missionVal = (p) => p.tjm * 110;
export const isHot = (p) => (p.stage === 'identifie' || p.stage === 'envoye') && p.days >= RELANCE_DAYS;
export const matchScore = (co, n) => {
  if (!n.skills || !n.skills.length) return 0;
  return Math.round((n.skills.filter((s) => co.skills.includes(s)).length / n.skills.length) * 100);
};
export const matchable = (co) => ['Disponible', 'Intercontrat', 'Bientôt dispo'].includes(co.statut);
export const isInterco = (co) =>
  co.statut === 'Intercontrat' ||
  ((co.statut === 'En mission' || co.statut === 'Bientôt dispo') && daysLeft(co.end) != null && daysLeft(co.end) <= INTERCO_DAYS);
export const isPlaceable = (co) => co.statut === 'Disponible' || co.statut === 'Intercontrat';
export const consDispo = (co) => {
  if (co.statut === 'Disponible') return 'Disponible immédiatement';
  if (co.statut === 'Intercontrat') return 'En intercontrat — à replacer';
  const dl = daysLeft(co.end);
  if (dl != null) return (co.statut === 'En mission' ? 'En mission @ ' + co.client : 'Bientôt dispo @ ' + co.client) + ' · fin dans ' + dl + ' j';
  return co.statut;
};

// ── Hiérarchie compte (helpers purs, clients = tableau des comptes) ──────────
export const clientOf = (clients, name) =>
  clients.find((c) => c.name === name) || { name, sector: '', city: '', contact: '', email: '', phone: '', clients: [] };
export const entitesOf = (clients, gc) => (clients.find((c) => c.name === gc) || {}).clients || [];
export const entiteObj = (clients, gc, en) => entitesOf(clients, gc).find((x) => x.name === en);
export const sousEntitesOf = (clients, gc, en) => {
  const c = entiteObj(clients, gc, en);
  return (c && c.entities) || [];
};
export const sousEntiteObj = (clients, gc, en, se) => sousEntitesOf(clients, gc, en).find((e) => e.name === se);
export const equipesOf = (clients, gc, en, se) => {
  const e = sousEntiteObj(clients, gc, en, se);
  return (e && e.subs) || [];
};
export const equipeObj = (clients, gc, en, se, eq) => equipesOf(clients, gc, en, se).find((x) => x.name === eq);
export const domainesOf = (clients, gc, en, se, eq) => {
  const s = equipeObj(clients, gc, en, se, eq);
  return (s && s.domains) || [];
};

export const candidatesFor = (pos, n) => pos.filter((p) => p.ao === n.title && p.client === n.client);
export const bestMatchFor = (cons, n) => cons.filter(matchable).reduce((m, co) => Math.max(m, matchScore(co, n)), 0);

// ── Parsing / sérialisation de la structure d'une entité ─────────────────────
// Format : « Sous-entité > Équipe : domaine1, domaine2 [ @ Contact <email> ] »
export function parseStructure(text) {
  const map = {};
  text.split('\n').map((l) => l.trim()).filter(Boolean).forEach((l) => {
    const ci = l.indexOf(':');
    const lhs = ci >= 0 ? l.slice(0, ci) : l;
    const rhs = ci >= 0 ? l.slice(ci + 1) : '';
    const parts = lhs.split('>').map((x) => x.trim());
    const se = parts[0] || '';
    if (!se) return;
    const eq = parts[1] || 'Général';
    let domStr = rhs; let contact = ''; let cemail = '';
    const at = rhs.split(' @ ');
    if (at.length > 1) {
      domStr = at[0];
      const cp = at.slice(1).join(' @ ').trim();
      const em = cp.match(/[\w.+-]+@[\w.-]+\.\w+/);
      if (em) { cemail = em[0]; contact = cp.replace(em[0], '').replace(/[<>]/g, '').trim(); } else contact = cp;
    }
    const doms = domStr.split(',').map((x) => x.trim()).filter(Boolean);
    map[se] = map[se] || { name: se, subs: {} };
    const sbm = (map[se].subs[eq] = map[se].subs[eq] || { name: eq, domains: [] });
    doms.forEach((d) => { if (!sbm.domains.includes(d)) sbm.domains.push(d); });
    if (contact) sbm.contact = contact;
    if (cemail) sbm.email = cemail;
  });
  return Object.values(map).map((e) => ({ name: e.name, subs: Object.values(e.subs) }));
}
export function serializeStructure(entities) {
  return (entities || [])
    .flatMap((e) =>
      (e.subs || []).length
        ? (e.subs || []).map(
            (sb) =>
              `${e.name} > ${sb.name} : ${(sb.domains || []).join(', ')}${
                sb.contact || sb.email
                  ? ' @ ' + [sb.contact || '', sb.email ? '<' + sb.email + '>' : ''].filter(Boolean).join(' ')
                  : ''
              }`
          )
        : [`${e.name} > Équipe : `]
    )
    .join('\n');
}

// ── Tutoriels contextuels par vue ────────────────────────────────────────────
export const TUTOS = {
  cockpit: { title: 'Tableau de bord — vos actions du jour', steps: [
    ['Tout ce qui demande une action', "Relances dues, entretiens, négos à closer, intercontrats, AOs urgents — rien d'autre."],
    ['Cliquez une ligne', 'Chaque ligne ouvre la fiche complète (positionnement, consultant ou AO).'],
    ['Agissez en un clic', '« Relancer » remet le compteur à zéro, « Positionner » lance le placement.'],
  ] },
  kanban: { title: 'Positionnements — votre pipeline de closing', steps: [
    ['1 carte = 1 consultant sur 1 besoin', 'Avec son TJM, son % de match et son commercial.'],
    ['Glissez-déposez entre colonnes', "L'étape change et l'historique se met à jour tout seul."],
    ['Bord rouge = relance due', 'Un DC envoyé sans réponse depuis 4 jours réclame une relance.'],
  ] },
  clients: { title: 'Comptes — vos grands comptes cartographiés', steps: [
    ['Un compte = un groupe entier', 'Ses entités (CIB, Retail…), leurs équipes et vos positionnements dedans.'],
    ['Cherchez un savoir-faire', 'Tapez « cash management » : les équipes qui le font ressortent, tous comptes confondus.'],
    ['Ouvrez un compte', 'Pour sa cartographie complète et envoyer un profil au bon endroit.'],
  ] },
  account: { title: "Fiche compte — trouvez l'équipe, envoyez le profil", steps: [
    ['Cherchez d’abord', 'Équipe, domaine ou contact — tapez « / » pour aller à la recherche.'],
    ['« Envoyer un profil »', 'Le formulaire arrive pré-rempli sur la bonne équipe, brouillon mail en option.'],
    ['Dépliez un résultat', 'Les profils dispo (intercontrats en tête) se proposent : « Positionner ici ».'],
    ['La structure en dessous', "L'organigramme complet, et « + Entité » pour cartographier le compte."],
  ] },
  consultants: { title: 'Consultants — votre vivier', steps: [
    ["Statut en un coup d'œil", 'Disponible, en mission, bientôt dispo… le point coloré vous guide.'],
    ['Rouge = à replacer', 'Intercontrat ou fin de mission sous 30 jours : agissez avant le bench.'],
    ['Positionnez direct', 'Depuis la fiche : « Positionner sur un AO », rôle et TJM déjà remplis.'],
  ] },
  matching: { title: 'Matching IA — le bon profil sur le bon besoin', steps: [
    ['% = compétences en commun', 'Entre un consultant disponible et un besoin ouvert.'],
    ["Intercontrats d'abord", 'Le filtre « Intercontrats à replacer » priorise ceux qui coûtent.'],
    ['« Positionner »', 'Crée le positionnement pré-rempli — il ne reste qu’à envoyer le DC.'],
  ] },
  ao: { title: "Appels d'offres — vos besoins à staffer", steps: [
    ['Triés par urgence', 'Échéance la plus proche en premier ; rouge = J-7 ou moins.'],
    ['« à staffer » = zéro candidat', "Ouvrez l'AO : les meilleurs profils dispo sont déjà suggérés."],
    ["Positionnez depuis l'AO", 'Un clic sur « Positionner » et le closing démarre.'],
  ] },
  shortlists: { title: 'Shortlists — vos sélections de candidats', steps: [
    ['1 shortlist = 1 besoin', 'Les meilleurs profils matchés sont proposés automatiquement.'],
    ["Affinez d'un clic", 'Basculez chaque candidat : proposé → retenu → écarté.'],
    ['« Envoyer au client »', 'Les retenus passent en « DC envoyé » dans le pipeline.'],
  ] },
};
